import { Switch as RadixSwitch, ToggleGroup } from 'radix-ui'
import type { ComponentProps, ReactNode } from 'react'
import { cn } from './cn.js'

export interface SegmentedProps<T extends string> {
  value: T
  onValueChange: (value: T) => void
  options: ReadonlyArray<{ value: T; label: ReactNode }>
  label: string
  className?: string
}

/** Mutually exclusive choice — meal slot, per-100g vs per-serving, week vs month. */
export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  label,
  className,
}: SegmentedProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      aria-label={label}
      onValueChange={(next) => {
        if (next) onValueChange(next as T)
      }}
      className={cn(
        'inline-flex h-9 items-center gap-0.5 rounded-[var(--radius-control)] border border-line bg-raised p-0.5',
        className,
      )}
    >
      {options.map((opt) => (
        <ToggleGroup.Item
          key={opt.value}
          value={opt.value}
          className={cn(
            // The transparent border is carried by every item so selecting one
            // colours an edge that already exists instead of adding one and
            // nudging the row by a pixel.
            'h-8 flex-1 rounded-[3px] border border-transparent px-3 text-sm text-dim transition-colors duration-[140ms]',
            'data-[state=on]:border-accent data-[state=on]:bg-accent/10 data-[state=on]:font-semibold data-[state=on]:text-accent-text',
          )}
        >
          {opt.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  )
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled = false,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  label: string
  /**
   * A switch that cannot be moved says so. Swallowing the tap instead is how a
   * control comes to be described as "sometimes it just doesn't work".
   */
  disabled?: boolean
}) {
  return (
    <RadixSwitch.Root
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
      className={cn(
        'relative h-6 w-10 shrink-0 rounded-full border transition-colors duration-[140ms]',
        // The visible toggle is 24px tall; the before-layer takes the tap
        // target to 44px without loosening the row.
        "before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-['']",
        disabled && 'opacity-40',
        checked ? 'border-accent bg-accent' : 'border-line bg-raised',
      )}
    >
      <RadixSwitch.Thumb
        className={cn(
          'block size-4 rounded-full bg-surface transition-transform duration-[140ms] ease-[var(--ease-out-quick)]',
          checked ? 'translate-x-[1.15rem]' : 'translate-x-0.5',
        )}
      />
    </RadixSwitch.Root>
  )
}

export function Chip({
  className,
  tone = 'neutral',
  ...props
}: ComponentProps<'span'> & { tone?: 'neutral' | 'accent' | 'warn' }) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-full border px-2 text-xs',
        tone === 'accent' && 'border-accent/40 text-accent-text',
        tone === 'warn' && 'border-warn/40 text-warn',
        tone === 'neutral' && 'border-line text-mute',
        className,
      )}
      {...props}
    />
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('size-4 animate-spin text-mute', className)}
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="2.5"
        fill="none"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}
